// =============================================================================
// Painel Financeiro de Ferramentas — a análise gerencial (067, 069)
//
// Pedido do Enzo em 22/10: o Painel era o Controle Geral ordenado por custo.
// Agora responde as perguntas da gestão, na ordem do design system (§3.6):
// indicadores → visão → rankings → tabela detalhada.
//
//   · Quanto gastamos, e para onde vai: área, conta, família, as mais caras.
//   · O que vem no caixa: o desembolso dos próximos 12 meses.
//   · Quanto está em dólar, e quanto o câmbio mexe.
//   · Cabe no orçamento: ferramentas × orçado do exercício, por área e conta.
//   · Onde arrumar ou economizar: governança, consolidação, custo por pessoa.
//
// As contas moram em src/lib/ferramentasAnalise.ts, testadas. O recorte de
// quem vê o quê é da RLS: o gestor vê o painel só dos CCs dele.
// =============================================================================

import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bar, BarChart, Cell, ComposedChart, Line, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { buscarOrcamentoFerramentas, definirAcrescimoCotacao, FalhaApi, type DadosFerramentas, type Ferramenta } from "../lib/api.ts";
import { competenciaCurta, moeda, moedaCompacta, percentual } from "../lib/formato.ts";
import {
  BarraConsumo, CabecalhoGrafico, COR, Kpi, LegendaGrafico, Painel, TooltipRico, Vazio,
} from "../componentes/ui.tsx";
import { CaixaGrade } from "../componentes/Grade.tsx";
import { COR_STATUS, rotuloStatus } from "../lib/ferramentas.ts";
import { textoAcrescimo, textoCotacao } from "../lib/ferramentaCiclo.ts";
import {
  chavesDe, cobradoPorMes, concentracao, contraOrcamento, desembolso12, governanca, mesFechado, pct, porArea, porCartao,
  porConta, porFamilia, porFornecedor, porResponsavel, resumir, ROTULO_FAMILIA, taxaEfetivaDolar, vivas, type Grupo,
  type LinhaFornecedor, type LinhaResponsavel,
} from "../lib/ferramentasAnalise.ts";
import { Faixa, TD, TH } from "./FerramentasAbas.tsx";

/** Paleta categórica: azul, laranja e os tons do design system CF. */
const PALETA = ["#183c77", "#ff9933", "#8ea0c4", "#3b6d11", "#5b7bb5", "#c3cbd9", "#a36a00", "#a32d2d"];

const hojeSP = () => new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Sao_Paulo", year: "numeric", month: "2-digit", day: "2-digit",
}).format(new Date());

const reais = (v: bigint) => moeda(v.toString());
const emNumero = (v: bigint) => Number(v) / 100;

export function AbaPainel({ dados, onEditar }: { dados: DadosFerramentas; onEditar: (f: Ferramenta) => void }) {
  const hoje = hojeSP();
  const anoAtual = Number(hoje.slice(0, 4));
  const [area, setArea] = useState("todas");
  const [ano, setAno] = useState(anoAtual);

  const areas = useMemo(() => porArea(dados.ferramentas).filter((g) => g.chave), [dados.ferramentas]);
  const fs = useMemo(() => (area === "todas" ? dados.ferramentas : dados.ferramentas.filter((f) => (f.centro_custo_id ?? "") === area)),
    [dados.ferramentas, area]);

  const resumo = useMemo(() => resumir(fs, hoje), [fs, hoje]);
  const conc = useMemo(() => concentracao(fs, 10), [fs]);
  const caixa = useMemo(() => desembolso12(fs, hoje), [fs, hoje]);
  const gov = useMemo(() => governanca(fs), [fs]);
  const orc = useQuery({ queryKey: ["ferramentas-orcamento", ano], queryFn: () => buscarOrcamentoFerramentas(ano) });
  const contraOrc = useMemo(() => contraOrcamento(fs, orc.data?.linhas ?? []), [fs, orc.data]);

  const pctUsd = pct(resumo.mensalUsd, resumo.mensal);

  // As cobranças reconhecidas (070): o recorte por área vale pelos fornecedores
  // das ferramentas da área.
  const fechado = mesFechado(hoje);
  const chaves = useMemo(() => (area === "todas" ? undefined : chavesDe(dados.cobrancas.grupos, fs)), [area, dados.cobrancas, fs]);
  const serieCobrado = useMemo(() => cobradoPorMes(dados.cobrancas.meses, fechado, 12, chaves), [dados.cobrancas, fechado, chaves]);
  const fornecedores = useMemo(() => porFornecedor(dados.cobrancas.grupos, dados.cobrancas.meses, fechado, chaves),
    [dados.cobrancas, fechado, chaves]);
  const taxa = useMemo(() => taxaEfetivaDolar(fs, dados.cobrancas.porFerramenta, fechado, dados.cotacao),
    [fs, dados.cobrancas, fechado, dados.cotacao]);
  const temCobranca = dados.cobrancas.meses.length > 0;

  return (
    <div className="grid gap-5">
      <Faixa>Painel financeiro — análise das ferramentas</Faixa>

      <div className="flex flex-wrap items-end gap-3">
        <label className="block text-[12px] font-semibold text-tinta-media">Área
          <select value={area} onChange={(e) => setArea(e.target.value)}
            className="mt-1 block rounded-lg border border-linha bg-superficie px-3 py-1.5 text-[13px]">
            <option value="todas">Todas as áreas</option>
            {areas.map((a) => <option key={a.chave} value={a.chave}>{a.rotulo}</option>)}
          </select>
        </label>
        <p className="pb-1.5 text-[12px] text-tinta-fraca">
          Ativas, fora as canceladas. {resumo.paraConferir > 0 && `${resumo.paraConferir} sem ciclo entram pelo mensal da planilha.`}
        </p>
      </div>

      {/* ── indicadores ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] gap-3">
        <Kpi rotulo="Custo mensal" valor={reais(resumo.mensal)} destaque
          apoio={`${resumo.ativas} ferramentas${resumo.semValor ? ` · ${resumo.semValor} sem valor` : ""}`}
          dica="A soma do Valor Mensal Estimado: o valor do ciclo ÷ meses do ciclo; em dólar, pela PTAX mais o acréscimo. Pontual (compra única) não entra." />
        <Kpi rotulo="Custo no ano" valor={reais(resumo.anual)} apoio="projetado pelo ciclo de cada uma"
          dica="O custo de 12 meses: o mensal × 12, o anual como é, e a compra pontual pelo valor dela." />
        <Kpi rotulo="Em dólar" valor={percentual(pctUsd)} apoio={`${reais(resumo.mensalUsd)} por mês`}
          dica="A parte do custo mensal cobrada em dólar: a que o câmbio mexe." />
        <Kpi rotulo="Renovam em 60 dias" valor={reais(resumo.renovacoes60.valor)}
          apoio={`${resumo.renovacoes60.n} ferramenta(s) com data`} tom={resumo.renovacoes60.n ? "atencao" : undefined}
          dica="As que têm a próxima renovação nos próximos 60 dias, pelo valor do ciclo. Só conta quem tem a data informada." />
        <Kpi rotulo="10 mais caras" valor={percentual(conc.pctTop)} apoio="do custo mensal"
          dica="Quanto do custo mensal as 10 ferramentas mais caras somam. Concentração alta: é nelas que está a economia." />
        <Kpi rotulo="Para conferir" valor={String(resumo.paraConferir)} tom={resumo.paraConferir ? "atencao" : "ok"}
          apoio="sem ciclo de cobrança" dica="Sem ciclo, a ferramenta entra no painel pelo mensal da planilha. Abra e escolha a periodicidade." />
      </div>
      <AcrescimoCotacao acrescimo={dados.cotacao?.acrescimo ?? null} />

      {/* ── cobrado × estimado (070) ─────────────────────────────────────── */}
      {temCobranca && (
        <>
          <Painel>
            <CabecalhoGrafico titulo="Cobrado × estimado"
              dica="O que o Conta Azul cobrou nas contas de ferramenta (com o IOF), mês a mês, contra o custo mensal estimado de hoje. Reconhecido toda madrugada." />
            <CobradoEstimado serie={serieCobrado} estimado={resumo.mensal} />
            <LegendaGrafico series={[["Cobrado no mês", COR.realizado], ["Estimado por mês (hoje)", COR.forecast]]} />
            {taxa && (
              <TaxaEfetiva taxa={taxa.taxa} acrescimo={taxa.acrescimo} amostras={taxa.amostras} atual={dados.cotacao?.acrescimo ?? null} />
            )}
          </Painel>
          <Painel>
            <CabecalhoGrafico titulo={`Por fornecedor — ${competenciaCurta(`${fechado}-01`)}`}
              dica="Cada fornecedor das cobranças, com as ferramentas do cadastro que casam com ele. A variação compara o cobrado no mês fechado com o estimado do grupo, só para quem cobra todo mês." />
            <TabelaFornecedores linhas={fornecedores} />
          </Painel>
        </>
      )}

      {/* ── para onde vai ────────────────────────────────────────────────── */}
      <div className="grid gap-5 lg:grid-cols-2">
        <Painel>
          <CabecalhoGrafico titulo="Custo mensal por área" dica="Por centro de custo. A barra é proporcional à maior; o percentual é do total." />
          <ListaBarras grupos={porArea(fs)} total={resumo.mensal} vazio="Nenhuma ferramenta com custo" />
        </Painel>
        <Painel>
          <CabecalhoGrafico titulo="Composição por conta contábil" dica="A conta do Conta Azul em que cada ferramenta é lançada." />
          <Rosca grupos={porConta(fs)} total={resumo.mensal} />
        </Painel>
        <Painel>
          <CabecalhoGrafico titulo="Custo mensal por família" dica="A função da ferramenta. Pré-classificada pelo tipo; o Financeiro corrige no cadastro." />
          <ListaBarras grupos={porFamilia(fs)} total={resumo.mensal} vazio="Nenhuma ferramenta com custo" />
        </Painel>
        <Painel>
          <CabecalhoGrafico titulo="As 10 mais caras" dica="Pelo custo mensal. Clique para abrir o cadastro." />
          <ListaBarras
            grupos={conc.lista.map((f) => ({ chave: f.id, rotulo: f.nome, mensal: BigInt(f.custo_mensal_centavos!), anual: 0n, n: 1, ferramentas: [f] }))}
            total={resumo.mensal} vazio="Nenhuma ferramenta com custo" onClicar={(g) => onEditar(g.ferramentas[0]!)} />
        </Painel>
      </div>

      {/* ── caixa ────────────────────────────────────────────────────────── */}
      <Painel>
        <CabecalhoGrafico titulo="Desembolso previsto nos próximos 12 meses"
          dica="O recorrente (mensal e por uso, todo mês) e as renovações (trimestral, semestral e anual, no mês em que renovam). É o que alimenta o fluxo de caixa." />
        <Desembolso dados={caixa.meses} />
        <LegendaGrafico series={[["Recorrente", COR.realizado], ["Renovações", COR.forecast]]} />
        {(caixa.semData.n > 0 || caixa.aproximadas > 0) && (
          <p className="mt-2 text-[12px] text-tinta-fraca">
            {caixa.semData.n > 0 && `${caixa.semData.n} anual(is) sem data de renovação somam ${reais(caixa.semData.valor)} no ano, em mês desconhecido. `}
            {caixa.aproximadas > 0 && `${caixa.aproximadas} entram pelo mensal equivalente (sem ciclo, ou sem a data da renovação).`}
          </p>
        )}
      </Painel>

      {/* ── dólar e cartões ──────────────────────────────────────────────── */}
      <div className="grid gap-5 lg:grid-cols-2">
        <Painel>
          <CabecalhoGrafico titulo="Exposição ao dólar" dica="O custo mensal em dólar, convertido pela PTAX mais o acréscimo, contra o em real." />
          <ExposicaoDolar real={resumo.mensal - resumo.mensalUsd} dolar={resumo.mensalUsd} cotacao={textoCotacao(dados.cotacao)} />
        </Painel>
        <Painel>
          <CabecalhoGrafico titulo="Custo por cartão" dica="Pelo final do cartão: para bater com a fatura de cada um." />
          <TabelaGrupos grupos={porCartao(fs)} vazio="Nenhuma ferramenta paga no cartão" />
        </Painel>
      </div>

      {/* ── orçamento ────────────────────────────────────────────────────── */}
      <Painel>
        <CabecalhoGrafico
          titulo="Ferramentas × orçamento"
          dica="Por área e conta: o custo projetado das ferramentas no ano contra o orçado do exercício na mesma conta, e o que já foi realizado nela (tudo o que foi lançado na conta, não só ferramenta)."
          acessorio={
            <select value={ano} onChange={(e) => setAno(Number(e.target.value))} aria-label="Exercício"
              className="rounded-lg border border-linha bg-superficie px-2 py-1 text-[12.5px]">
              {[anoAtual - 1, anoAtual, anoAtual + 1].map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          }
        />
        <ContraOrcamento linhas={contraOrc.linhas} semCoordenada={contraOrc.semCoordenada} carregando={orc.isPending}
          erro={orc.isError ? (orc.error as FalhaApi).message : null} ano={ano} />
      </Painel>

      {/* ── governança e economia ────────────────────────────────────────── */}
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        <ListaCurta titulo="Para conferir" dica="Sem ciclo de cobrança: o valor vem da planilha. Abra e escolha a periodicidade."
          itens={gov.paraConferir} onEditar={onEditar} />
        <ListaCurta titulo="A tendência pede revisão" dica="A “Obs / Tendência” da planilha fala em reduzir, cancelar ou rever."
          itens={gov.revisar} onEditar={onEditar} detalhe={(f) => f.tendencia} />
        <ListaCurta titulo="Sem responsável" dica="Ninguém responde pela ferramenta: quem decide renovar ou cancelar?"
          itens={gov.semResponsavel} onEditar={onEditar} />
        <ListaCurta titulo="Sem centro de custo ou sem conta" dica="Fica fora da análise por área e do confronto com o orçamento."
          itens={[...new Set([...gov.semCentro, ...gov.semConta])]} onEditar={onEditar}
          detalhe={(f) => [!f.centro_custo_id && "sem CC", !f.categoria_id && "sem conta"].filter(Boolean).join(" · ")} />
        <Painel>
          <CabecalhoGrafico titulo="Onde consolidar" dica="Famílias com mais de uma ferramenta em mais de uma área: a mesma função paga mais de uma vez." />
          {gov.consolidar.length === 0 ? <Vazio titulo="Nenhuma família repetida entre áreas" /> : (
            <ul className="grid gap-2 text-[12.5px]">
              {gov.consolidar.map((c) => (
                <li key={c.familia} className="flex items-baseline justify-between gap-3 border-t border-linha pt-2 first:border-t-0 first:pt-0">
                  <span><b>{c.rotulo}</b> <span className="text-tinta-fraca">· {c.n} ferramentas em {c.areas} áreas</span></span>
                  <span className="num shrink-0 font-semibold">{reais(c.mensal)}</span>
                </li>
              ))}
            </ul>
          )}
        </Painel>
        <Painel>
          <CabecalhoGrafico titulo="Custo por pessoa" dica="O custo mensal ÷ quem usa, onde o “Nº Usuários Ativos” está preenchido (um número ou a lista de nomes)." />
          {gov.porUsuario.length === 0 ? <Vazio titulo="Ninguém informado em “Nº Usuários Ativos”" dica="Preencha no cadastro para ver o custo por pessoa." /> : (
            <ul className="thin-scroll grid max-h-[260px] gap-1.5 overflow-y-auto pr-1 text-[12.5px]">
              {gov.porUsuario.slice(0, 15).map((x) => (
                <li key={x.f.id} className="flex items-baseline justify-between gap-3">
                  <button type="button" onClick={() => onEditar(x.f)} className="truncate text-left hover:underline">{x.f.nome}</button>
                  <span className="num shrink-0"><b>{reais(x.porUsuario)}</b> <span className="text-tinta-fraca">· {x.usuarios} pessoa(s)</span></span>
                </li>
              ))}
            </ul>
          )}
        </Painel>
      </div>

      {/* ── por responsável (072) ────────────────────────────────────────── */}
      <Painel>
        <CabecalhoGrafico titulo="Por responsável"
          dica="Cada pessoa responsável, as ferramentas pelas quais responde e o custo delas. A ferramenta com mais de um responsável entra inteira em cada um: por isso a soma das linhas passa do total." />
        <PorResponsavel linhas={porResponsavel(fs)} semResponsavel={gov.semResponsavel.length} onEditar={onEditar} />
      </Painel>

      {/* ── detalhe ──────────────────────────────────────────────────────── */}
      <TabelaDetalhada fs={fs} onEditar={onEditar} />
    </div>
  );
}

// ── peças ──────────────────────────────────────────────────────────────────

export function CobradoEstimado({ serie, estimado }: { serie: { mes: string; cobrado: bigint }[]; estimado: bigint }) {
  // O estimado entra como série, e não como linha de referência: o eixo passa a
  // contar com ele, e a linha aparece mesmo acima do maior cobrado.
  const dados = serie.map((x) => ({ mes: competenciaCurta(`${x.mes}-01`), cobrado: emNumero(x.cobrado), estimado: emNumero(estimado), x }));
  return (
    <div className="h-[240px] w-full">
      <ResponsiveContainer>
        <ComposedChart data={dados} margin={{ top: 8, right: 8, left: 8 }}>
          <XAxis dataKey="mes" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#6b7789" }} />
          <YAxis tickLine={false} axisLine={false} width={68} tick={{ fontSize: 11, fill: "#6b7789" }}
            tickFormatter={(v: number) => moedaCompacta(String(Math.round(v * 100)))} />
          <Tooltip cursor={{ fill: "rgba(16,40,80,0.04)" }} content={({ active, payload, label }) => {
            const p = payload?.[0]?.payload as (typeof dados)[number] | undefined;
            if (!active || !p) return null;
            const dif = estimado > 0n ? pct(p.x.cobrado - estimado, estimado) : null;
            return <TooltipRico titulo={String(label)} itens={[
              { rotulo: "Cobrado", valor: reais(p.x.cobrado), cor: COR.realizado },
              { rotulo: "Estimado (hoje)", valor: reais(estimado), cor: COR.forecast },
              ...(dif !== null ? [{ rotulo: "Diferença", valor: `${dif > 0 ? "+" : ""}${percentual(dif)}` }] : []),
            ]} />;
          }} />
          <Bar dataKey="cobrado" fill={COR.realizado} radius={[2, 2, 0, 0]} maxBarSize={32} />
          <Line dataKey="estimado" stroke={COR.forecast} strokeDasharray="4 3" strokeWidth={2} dot={false} activeDot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/** O que o cartão cobrou por dólar, e o botão de usar o acréscimo medido. */
function TaxaEfetiva({ taxa, acrescimo, amostras, atual }: { taxa: number; acrescimo: number | null; amostras: number; atual: string | null }) {
  const qc = useQueryClient();
  const usar = useMutation({
    mutationFn: (v: number) => definirAcrescimoCotacao(Math.round(v * 10000) / 10000),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ferramentas"] }),
  });
  const real = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 4, maximumFractionDigits: 4 });
  const dentro = acrescimo !== null && acrescimo >= 0 && acrescimo <= 0.5;
  return (
    <p className="mt-3 rounded-lg bg-superficie-2 px-3 py-2 text-[12.5px]">
      Nos 3 meses fechados, o cartão cobrou em média <b className="num">{real(taxa)}</b> por dólar
      {acrescimo !== null && <> — a PTAX <b>{acrescimo >= 0 ? "+" : ""}{percentual(acrescimo * 100)}</b></>}
      {" "}({amostras} cobrança(s) de ferramenta em dólar, com o IOF).
      {atual !== null && <> O painel usa hoje <b>+{textoAcrescimo(atual)}</b>.</>}
      {dentro && acrescimo !== null && atual !== null && Math.abs(acrescimo - Number(atual)) > 0.005 && (
        <button type="button" disabled={usar.isPending} onClick={() => usar.mutate(acrescimo)}
          className="ml-2 rounded border border-linha bg-superficie px-2 py-0.5 text-[11.5px] font-semibold hover:border-laranja disabled:opacity-50">
          {usar.isPending ? "Salvando…" : `Usar +${percentual(acrescimo * 100)}`}
        </button>
      )}
      {usar.isError && <span className="ml-2 text-risco-tinta">{(usar.error as FalhaApi).message}</span>}
    </p>
  );
}

function TabelaFornecedores({ linhas }: { linhas: LinhaFornecedor[] }) {
  if (linhas.length === 0) return <Vazio titulo="Nenhuma cobrança reconhecida nos últimos meses" />;
  return (
    <div className="thin-scroll max-h-[420px] overflow-auto">
      <table className="w-full min-w-[640px] text-[12.5px]">
        <thead className="sticky top-0 bg-superficie text-left text-[11px] text-tinta-fraca">
          <tr>
            <th className="pb-1.5">Fornecedor</th><th className="pb-1.5">Ferramentas</th>
            <th className="pb-1.5 text-right">Estimado/mês</th><th className="pb-1.5 text-right">Cobrado</th>
            <th className="pb-1.5 text-right">Variação</th><th className="pb-1.5 text-right">Média 3 meses</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => {
            const tom = l.variacao === null ? "" : Math.abs(l.variacao) > 30 ? "text-risco-tinta font-semibold" : "text-tinta-media";
            return (
              <tr key={l.chave} className="border-t border-linha">
                <td className="py-1.5 font-semibold">{l.chave.toUpperCase()}</td>
                <td className="py-1.5">
                  {l.ativas > 0 ? `${l.ativas} ativa(s)` : l.canceladas > 0
                    ? <span className="text-risco-tinta">só canceladas</span> : <span className="text-atencao-tinta">sem ferramenta</span>}
                </td>
                <td className="num py-1.5 text-right">{l.esperado > 0n ? reais(l.esperado) : "—"}</td>
                <td className="num py-1.5 text-right font-semibold">{l.ultimo > 0n ? reais(l.ultimo) : "—"}</td>
                <td className={`num py-1.5 text-right ${tom}`}>{l.variacao === null ? "—" : `${l.variacao > 0 ? "+" : ""}${percentual(l.variacao, 0)}`}</td>
                <td className="num py-1.5 text-right">{reais(l.media3)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Barras em lista: o nome encostado no valor (§3.6, nomes longos). */
function ListaBarras({ grupos, total, vazio, onClicar }: {
  grupos: Grupo[];
  total: bigint;
  vazio: string;
  onClicar?: (g: Grupo) => void;
}) {
  const com = grupos.filter((g) => g.mensal > 0n);
  if (com.length === 0 || total === 0n) return <Vazio titulo={vazio} />;
  const maior = com[0]!.mensal;
  return (
    <ul className="thin-scroll max-h-[320px] overflow-y-auto pr-1">
      {com.map((g) => (
        <li key={g.chave || g.rotulo} className="border-t border-linha py-2 first:border-t-0">
          <div className="flex items-baseline justify-between gap-3">
            {onClicar ? (
              <button type="button" onClick={() => onClicar(g)} className="truncate text-left text-[13px] font-medium hover:underline" title={g.rotulo}>{g.rotulo}</button>
            ) : (
              <span className="truncate text-[13px] font-medium" title={g.rotulo}>
                {g.rotulo}{g.n > 1 && <span className="text-tinta-fraca"> · {g.n}</span>}
              </span>
            )}
            <span className="num shrink-0 text-[12.5px]"><b>{reais(g.mensal)}</b> <span className="text-tinta-fraca">{percentual(pct(g.mensal, total))}</span></span>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-superficie-2">
            <div className="h-full rounded-full bg-azul-medio" style={{ width: `${Number((g.mensal * 1000n) / maior) / 10}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Rosca com o total no centro e legenda própria (§3.6). */
function Rosca({ grupos, total }: { grupos: Grupo[]; total: bigint }) {
  const com = grupos.filter((g) => g.mensal > 0n);
  if (com.length === 0) return <Vazio titulo="Nenhuma ferramenta com custo" />;
  const dados = com.map((g, i) => ({ nome: g.rotulo, valor: emNumero(g.mensal), cor: PALETA[i % PALETA.length]!, g }));
  return (
    <div className="grid items-center gap-4 sm:grid-cols-[180px_1fr]">
      <div className="relative h-[180px]">
        <ResponsiveContainer>
          <PieChart>
            <Pie data={dados} dataKey="valor" nameKey="nome" innerRadius={58} outerRadius={84} paddingAngle={1} stroke="none">
              {dados.map((d) => <Cell key={d.nome} fill={d.cor} />)}
            </Pie>
            <Tooltip content={({ active, payload }) => {
              const p = payload?.[0]?.payload as (typeof dados)[number] | undefined;
              return active && p ? <TooltipRico titulo={p.nome} itens={[{ rotulo: "Por mês", valor: reais(p.g.mensal) },
                { rotulo: "Do total", valor: percentual(pct(p.g.mensal, total)) }, { rotulo: "Ferramentas", valor: String(p.g.n) }]} /> : null;
            }} />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 grid place-items-center text-center">
          <div><p className="text-[10.5px] tracking-wide text-tinta-fraca uppercase">Por mês</p><p className="num text-[15px] font-bold">{moedaCompacta(total.toString())}</p></div>
        </div>
      </div>
      <ul className="grid gap-1.5 text-[12.5px]">
        {dados.map((d) => (
          <li key={d.nome} className="flex items-baseline justify-between gap-3">
            <span className="flex min-w-0 items-center gap-1.5"><span aria-hidden className="size-2.5 shrink-0 rounded-sm" style={{ background: d.cor }} />
              <span className="truncate" title={d.nome}>{d.nome}</span></span>
            <span className="num shrink-0 font-semibold">{percentual(pct(d.g.mensal, total))}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Desembolso({ dados }: { dados: { mes: string; recorrente: bigint; renovacoes: bigint }[] }) {
  const serie = dados.map((d) => ({ mes: competenciaCurta(`${d.mes}-01`), recorrente: emNumero(d.recorrente), renovacoes: emNumero(d.renovacoes), d }));
  if (dados.every((d) => d.recorrente === 0n && d.renovacoes === 0n)) return <Vazio titulo="Nada previsto" dica="Nenhuma ferramenta ativa com valor." />;
  return (
    <div className="h-[260px] w-full">
      <ResponsiveContainer>
        <BarChart data={serie} margin={{ top: 8, right: 8, left: 8 }}>
          <XAxis dataKey="mes" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#6b7789" }} />
          <YAxis tickLine={false} axisLine={false} width={68} tick={{ fontSize: 11, fill: "#6b7789" }}
            tickFormatter={(v: number) => moedaCompacta(String(Math.round(v * 100)))} />
          <Tooltip cursor={{ fill: "rgba(16,40,80,0.04)" }} content={({ active, payload, label }) => {
            const p = payload?.[0]?.payload as (typeof serie)[number] | undefined;
            return active && p ? <TooltipRico titulo={String(label)} itens={[
              { rotulo: "Recorrente", valor: reais(p.d.recorrente), cor: COR.realizado },
              { rotulo: "Renovações", valor: reais(p.d.renovacoes), cor: COR.forecast },
              { rotulo: "Total", valor: reais(p.d.recorrente + p.d.renovacoes) },
            ]} /> : null;
          }} />
          <Bar dataKey="recorrente" stackId="a" fill={COR.realizado} maxBarSize={32} />
          <Bar dataKey="renovacoes" stackId="a" fill={COR.forecast} radius={[2, 2, 0, 0]} maxBarSize={32} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ExposicaoDolar({ real, dolar, cotacao }: { real: bigint; dolar: bigint; cotacao: string | null }) {
  const total = real + dolar;
  if (total === 0n) return <Vazio titulo="Nenhuma ferramenta com custo" />;
  const dados = [{ nome: "Em real", valor: emNumero(real), cor: COR.orcado }, { nome: "Em dólar", valor: emNumero(dolar), cor: COR.realizado }];
  // O que +10% no dólar faz no custo: o custo em dólar já está em reais, então é 10% dele.
  const sensivel = dolar / 10n;
  return (
    <div className="grid items-center gap-4 sm:grid-cols-[160px_1fr]">
      <div className="relative h-[160px]">
        <ResponsiveContainer>
          <PieChart>
            <Pie data={dados} dataKey="valor" nameKey="nome" innerRadius={52} outerRadius={76} paddingAngle={1} stroke="none">
              {dados.map((d) => <Cell key={d.nome} fill={d.cor} />)}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 grid place-items-center text-center">
          <div><p className="text-[10.5px] tracking-wide text-tinta-fraca uppercase">Em dólar</p><p className="num text-[16px] font-bold">{percentual(pct(dolar, total))}</p></div>
        </div>
      </div>
      <div className="grid gap-2 text-[12.5px]">
        <p><span aria-hidden className="mr-1.5 inline-block size-2.5 rounded-sm" style={{ background: COR.realizado }} />Em dólar: <b className="num">{reais(dolar)}</b> por mês</p>
        <p><span aria-hidden className="mr-1.5 inline-block size-2.5 rounded-sm" style={{ background: COR.orcado }} />Em real: <b className="num">{reais(real)}</b> por mês</p>
        <p className="rounded-lg bg-superficie-2 px-3 py-2">
          Se o dólar subir <b>10%</b>, o custo sobe <b className="num">{reais(sensivel)}</b> por mês, <b className="num">{reais(sensivel * 12n)}</b> no ano.
        </p>
        <p className="text-[11.5px] text-tinta-fraca">{cotacao ?? "Sem cotação do dólar gravada ainda."}</p>
      </div>
    </div>
  );
}

function TabelaGrupos({ grupos, vazio }: { grupos: Grupo[]; vazio: string }) {
  if (grupos.length === 0) return <Vazio titulo={vazio} />;
  return (
    <table className="w-full text-[12.5px]">
      <thead className="text-left text-[11px] text-tinta-fraca"><tr><th className="pb-1.5">Cartão</th><th className="pb-1.5 text-right">Ferramentas</th><th className="pb-1.5 text-right">Por mês</th></tr></thead>
      <tbody>
        {grupos.map((g) => (
          <tr key={g.chave || g.rotulo} className="border-t border-linha">
            <td className="py-1.5">{g.rotulo}</td><td className="num py-1.5 text-right">{g.n}</td><td className="num py-1.5 text-right font-semibold">{reais(g.mensal)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ContraOrcamento({ linhas, semCoordenada, carregando, erro, ano }: {
  linhas: ReturnType<typeof contraOrcamento>["linhas"];
  semCoordenada: number;
  carregando: boolean;
  erro: string | null;
  ano: number;
}) {
  if (carregando) return <p className="text-[12.5px] text-tinta-fraca">Carregando o orçamento…</p>;
  if (erro) return <p className="text-[12.5px] text-risco-tinta">{erro}</p>;
  if (linhas.length === 0) return <Vazio titulo="Nenhuma ferramenta com centro de custo e conta" />;
  return (
    <>
      <div className="thin-scroll overflow-x-auto">
        <table className="w-full min-w-[720px] text-[12.5px]">
          <thead className="text-left text-[11px] text-tinta-fraca">
            <tr>
              <th className="pb-1.5">Área</th><th className="pb-1.5">Conta</th><th className="pb-1.5 text-right">Ferramentas</th>
              <th className="pb-1.5 text-right">Custo no ano</th><th className="pb-1.5 text-right">Orçado {ano}</th>
              <th className="w-[160px] pb-1.5 pl-4">% do orçado</th><th className="pb-1.5 text-right">Realizado na conta</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => {
              const p = l.orcado > 0n ? pct(l.anual, l.orcado) : null;
              const tom = p === null ? "risco" : p > 100 ? "risco" : p > 80 ? "atencao" : "ok";
              return (
                <tr key={l.chave} className="border-t border-linha align-middle">
                  <td className="py-1.5 pr-3 whitespace-nowrap">{l.area}</td>
                  <td className="max-w-[260px] truncate py-1.5 pr-3" title={l.conta}>{l.conta}</td>
                  <td className="num py-1.5 text-right">{l.n}</td>
                  <td className="num py-1.5 text-right font-semibold">{reais(l.anual)}</td>
                  <td className="num py-1.5 text-right">{l.orcado > 0n ? reais(l.orcado) : <span className="text-risco-tinta">sem orçado</span>}</td>
                  <td className="py-1.5 pl-4">
                    {p === null ? <span className="text-[11.5px] text-risco-tinta">não orçado</span> : (
                      <div className="flex items-center gap-2"><div className="flex-1"><BarraConsumo pct={p} tom={tom} /></div>
                        <span className="num w-12 text-right text-[11.5px]">{percentual(p, 0)}</span></div>
                    )}
                  </td>
                  <td className="num py-1.5 text-right">{reais(l.realizado)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {semCoordenada > 0 && (
        <p className="mt-2 text-[12px] text-tinta-fraca">{semCoordenada} ferramenta(s) sem centro de custo ou sem conta ficam fora desta conta.</p>
      )}
    </>
  );
}

/** O panorama por responsável (072): a pessoa abre e mostra as ferramentas dela. */
function PorResponsavel({ linhas, semResponsavel, onEditar }: {
  linhas: LinhaResponsavel[];
  semResponsavel: number;
  onEditar: (f: Ferramenta) => void;
}) {
  const [aberta, setAberta] = useState<string | null>(null);
  if (linhas.length === 0) return <Vazio titulo="Nenhuma ferramenta com responsável" dica="Preencha o responsável no cadastro." />;
  return (
    <>
      <div className="thin-scroll max-h-[440px] overflow-auto">
        <table className="w-full min-w-[560px] text-[12.5px]">
          <thead className="sticky top-0 bg-superficie text-left text-[11px] text-tinta-fraca">
            <tr>
              <th className="pb-1.5">Responsável</th><th className="pb-1.5 text-right">Ferramentas</th>
              <th className="pb-1.5 text-right">Por mês</th><th className="pb-1.5 text-right">No ano</th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <PorResponsavelLinha key={l.chave} l={l} aberta={aberta === l.chave}
                onAlternar={() => setAberta(aberta === l.chave ? null : l.chave)} onEditar={onEditar} />
            ))}
          </tbody>
        </table>
      </div>
      {semResponsavel > 0 && <p className="mt-2 text-[12px] text-tinta-fraca">{semResponsavel} ferramenta(s) sem responsável ficam de fora.</p>}
    </>
  );
}

function PorResponsavelLinha({ l, aberta, onAlternar, onEditar }: {
  l: LinhaResponsavel; aberta: boolean; onAlternar: () => void; onEditar: (f: Ferramenta) => void;
}) {
  return (
    <>
      <tr className="border-t border-linha">
        <td className="py-1.5">
          <button type="button" onClick={onAlternar} className="text-left font-semibold hover:underline" aria-expanded={aberta}>
            {aberta ? "▾" : "▸"} {l.nome}
          </button>
          {l.compartilhadas > 0 && <span className="ml-2 text-[11px] text-tinta-fraca">{l.compartilhadas} compartilhada(s)</span>}
        </td>
        <td className="num py-1.5 text-right">{l.n}</td>
        <td className="num py-1.5 text-right font-semibold">{reais(l.mensal)}</td>
        <td className="num py-1.5 text-right">{reais(l.anual)}</td>
      </tr>
      {aberta && l.ferramentas.map((f) => (
        <tr key={f.id} className="text-tinta-media">
          <td className="py-1 pl-5">
            <button type="button" onClick={() => onEditar(f)} className="text-left hover:underline">{f.nome}</button>
            {f.centro_custo_codigo && <span className="ml-2 text-[11px] text-tinta-fraca">{f.centro_custo_codigo} · {f.centro_custo_nome}</span>}
          </td>
          <td />
          <td className="num py-1 text-right">{f.custo_mensal_centavos ? moeda(f.custo_mensal_centavos) : "—"}</td>
          <td />
        </tr>
      ))}
    </>
  );
}

function ListaCurta({ titulo, dica, itens, onEditar, detalhe }: {
  titulo: string;
  dica: string;
  itens: Ferramenta[];
  onEditar: (f: Ferramenta) => void;
  detalhe?: (f: Ferramenta) => ReactNode;
}) {
  return (
    <Painel>
      <CabecalhoGrafico titulo={titulo} dica={dica}
        acessorio={<span className={`num rounded-full px-2 py-0.5 text-[12px] font-bold ${itens.length ? "bg-atencao-bg text-atencao-tinta" : "bg-ok-bg text-ok-tinta"}`}>{itens.length}</span>} />
      {itens.length === 0 ? <p className="text-[12.5px] text-tinta-fraca">Nenhuma. 👍</p> : (
        <ul className="thin-scroll grid max-h-[220px] gap-1.5 overflow-y-auto pr-1 text-[12.5px]">
          {itens.map((f) => (
            <li key={f.id}>
              <button type="button" onClick={() => onEditar(f)} className="text-left font-medium hover:underline">{f.nome}</button>
              {detalhe && detalhe(f) && <span className="block truncate text-[11.5px] text-tinta-fraca" title={String(detalhe(f))}>{detalhe(f)}</span>}
            </li>
          ))}
        </ul>
      )}
    </Painel>
  );
}

function TabelaDetalhada({ fs, onEditar }: { fs: Ferramenta[]; onEditar: (f: Ferramenta) => void }) {
  const v = vivas(fs);
  const ordenadas = [...v].sort((a, b) => {
    const va = a.custo_mensal_centavos ? BigInt(a.custo_mensal_centavos) : -1n;
    const vb = b.custo_mensal_centavos ? BigInt(b.custo_mensal_centavos) : -1n;
    return vb > va ? 1 : vb < va ? -1 : a.nome.localeCompare(b.nome);
  });
  const total = v.reduce((s, f) => s + (f.custo_mensal_centavos ? BigInt(f.custo_mensal_centavos) : 0n), 0n);
  return (
    <section>
      <h2 className="mb-2 font-display text-[16px] font-bold tracking-tight">Todas as ferramentas, por custo</h2>
      <CaixaGrade>
        <table className="w-full border-separate border-spacing-0 text-[12.5px]">
          <thead className="sticky top-0 z-10 bg-superficie-2 text-left text-[11px] font-semibold tracking-wide text-tinta-fraca">
            <tr>
              <th className={TH}>Ferramenta</th><th className={TH}>Família</th><th className={TH}>Centro de Custo</th>
              <th className={TH}>Moeda</th><th className={`${TH} text-right`}>Valor Mensal Estimado (R$)</th>
              <th className={TH}>Periodicidade</th><th className={TH}>Status</th><th className={TH}>Obs / Tendência</th>
            </tr>
          </thead>
          <tbody>
            {ordenadas.map((f) => (
              <tr key={f.id} onClick={() => onEditar(f)} className={`cursor-pointer hover:bg-superficie-2/60 ${COR_STATUS[f.status].linha}`}>
                <td className={`${TD} font-semibold text-tinta`}>{f.nome}</td>
                <td className={`${TD} whitespace-nowrap`}>{ROTULO_FAMILIA[f.familia ?? "outros"]}</td>
                <td className={`${TD} whitespace-nowrap`}>{f.centro_custo_nome ?? <span className="text-atencao-tinta">sem CC</span>}</td>
                <td className={TD}>{f.moeda}</td>
                <td className={`${TD} num text-right whitespace-nowrap`}>
                  {f.custo_mensal_centavos ? moeda(f.custo_mensal_centavos)
                    : <span className="text-tinta-media italic">{f.ciclo === "pontual" ? "pontual" : f.texto_planilha?.valor_mensal_centavos ?? "—"}</span>}
                </td>
                <td className={`${TD} max-w-[160px] truncate`}>{f.periodicidade}</td>
                <td className={`${TD} whitespace-nowrap`}>
                  <span className={`rounded px-1.5 py-0.5 text-[11.5px] font-semibold ${COR_STATUS[f.status].celula}`}>{rotuloStatus(f)}</span>
                </td>
                <td className={`${TD} min-w-[240px]`}>{f.tendencia}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="sticky bottom-0 bg-superficie-2 font-semibold">
            <tr>
              <td className="px-3 py-2" colSpan={4}>Total ({v.length} ativas)</td>
              <td className="num px-3 py-2 text-right">{moeda(total.toString())}</td>
              <td colSpan={3} />
            </tr>
          </tfoot>
        </table>
      </CaixaGrade>
    </section>
  );
}

/**
 * O acréscimo sobre a PTAX (067): o IOF de compra internacional e, se o
 * Financeiro quiser, o spread do banco. Mudar refaz o real de toda ferramenta
 * em dólar na hora, e fica no Histórico.
 */
function AcrescimoCotacao({ acrescimo }: { acrescimo: string | null }) {
  const qc = useQueryClient();
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState("");
  const salvar = useMutation({
    mutationFn: (p: number) => definirAcrescimoCotacao(p / 100),
    onSuccess: () => { setEditando(false); void qc.invalidateQueries({ queryKey: ["ferramentas"] }); },
  });
  if (acrescimo === null) return null;
  const p = Number(texto.replace(",", "."));
  const valido = texto.trim() !== "" && Number.isFinite(p) && p >= 0 && p <= 50;
  return (
    <div className="-mt-2 flex flex-wrap items-center gap-2 text-[12.5px] text-tinta-media">
      <span>Ferramenta em dólar: PTAX do Banco Central + <b>{textoAcrescimo(acrescimo)}</b> (IOF de compra internacional{Number(acrescimo) > 0.035 ? " e spread do banco" : ""}).</span>
      {!editando ? (
        <button type="button" className="text-[12px] font-semibold underline" onClick={() => { setTexto(String(Number(acrescimo) * 100).replace(".", ",")); setEditando(true); }}>
          alterar
        </button>
      ) : (
        <>
          <input value={texto} onChange={(e) => setTexto(e.target.value)} inputMode="decimal" aria-label="Acréscimo em %"
            className="num w-20 rounded-lg border border-linha bg-superficie px-2 py-1 text-right" />
          <span>%</span>
          <button type="button" disabled={!valido || salvar.isPending} onClick={() => salvar.mutate(p)}
            className="rounded-lg bg-laranja px-2.5 py-1 text-[12px] font-bold text-azul disabled:opacity-45">
            {salvar.isPending ? "Salvando…" : "Salvar"}
          </button>
          <button type="button" className="text-[12px]" onClick={() => setEditando(false)}>Cancelar</button>
        </>
      )}
      {salvar.isError && <span className="text-risco-tinta">{(salvar.error as FalhaApi).message}</span>}
    </div>
  );
}
